/**
 * @fileoverview Defines the command-line interface for alpha-wolf, an AI-powered developer tool. It uses `yargs` to parse commands for code generation (`generateCodeWithStreamV2`), documentation (`documentFiles`), and other tasks. Key classes and types include `ClaudeHaikuModel` and `Model`, with AI model configurations specified in `models`.
 */

import { ClaudeHaikuModel, ClaudeSonnetModel } from '@iterator/shared-node/ai/anthropic'
import { ContextProviderApiKeys, ContextProviders } from '@src/context'
import { doFix } from '@src/fix'
import { generateCodeWithStreamV2 } from '@src/generate/generate_v2'
import { paste } from 'copy-paste'
import yargs from 'yargs'
import { hideBin } from 'yargs/helpers'
import { runAgentLoop } from './src/agent_loop'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { askQuestion } from './src/ask'
import { writeCommitMessage } from './src/commit'
import { documentFiles } from './src/document'
import { finishDiff } from './src/finish'
import { UndoRedo } from './src/history'
import {
  DefaultEmbedderName,
  doEmbeddingIndex,
  EmbeddingName,
} from './src/indexing/index_embedding'
import { doLexicalIndex } from './src/indexing/index_lexical'
import {
  debug,
  debug_block,
  exit_with_error,
  info_block,
  print_ai,
} from '@iterator/shared-node/util/logging'
import { requireSettings, setupSettings } from './src/settings'
import { completeTodos } from './src/todo'
import { watchDirectory, WatchMode } from './src/watch'
import { generateChangeLog } from './src/change-log'
import { printContextTypes, printWatchModes } from './manual'
import { models } from '@src/ai/models'
import { DefaultModel } from '@src/ai/models'
import path from 'path'
import { Gpt5Model } from '@node_modules/@iterator/shared-node/ai/openai'

// Inquirer.js has an unhandled promise rejection when sigint is sent by the user.
process.on('unhandledRejection', (reason: any) => {
  if (reason.name === 'ExitPromptError') {
    debug('User exited prompt.', reason)
  } else {
    exit_with_error(reason)
  }
})

const commonOptions = (args: any) => {
  return args
    .option('directory', {
      type: 'string',
      describe: 'The target directory. Default: current directory',
      alias: 'd',
    })
    .option('v', {
      boolean: true,
      default: false,
      alias: 'verbose',
      describe: 'Enable verbose output. Can also be set with VERBOSE=true env var.',
    })
    .help('h')
    .alias('h', 'help')
}

const llmOptions = (args: any, modelName = DefaultModel.name) => {
  return args
    .option('model', {
      type: 'string',
      describe: 'The AI model to use. Default: ' + modelName,
      default: modelName,
      choices: Object.keys(models),
      alias: 'm',
    })
    .option('dry-run', {
      boolean: true,
      default: false,
      describe: 'Enable dry-run mode. Can also be set with DRY_RUN=true env var.',
    })
}

const indexOptions = (args: any) => {
  return args.option('query', {
    type: 'string',
    describe: 'The query to test the index with after its constructed.',
    alias: 'q',
  })
}

const handleCommand = (
  argv: any,
  action: (directory: string, prompt: string, model: Model, check?: string) => void,
) => {
  const verbose = argv.verbose as boolean
  process.env.VERBOSE = verbose.toString()
  debug_block('argv', argv)

  const dryRun = argv.dryRun as boolean
  if (dryRun) {
    process.env.DRY_RUN = dryRun.toString()
    print_ai('Dry run mode enabled. Skipping API calls.')
  }

  const directory = (argv.directory as string) || process.cwd()
  let prompt = argv.prompt as string
  let modelName = (argv.model as string) || process.env.AI_MODEL

  if (Array.isArray(prompt)) {
    prompt = prompt.join('\n')
  }

  if (argv.paste) {
    const pb = paste()
    debug_block('Adding paste buffer', pb)
    prompt = (prompt || '') + '\n' + pb
  }

  const model = models[modelName as keyof typeof models]
  debug('Using model:', model?.name, model)
  action(directory, prompt, model!, argv.check as string)
}

yargs(hideBin(process.argv))
  .usage('$0 <command> [options]')
  .epilog(
    '~~~~~~~~~~~~~~~~\n🐺 AlphaWolf 🐺\n\nThis is an exploration of AI development tools that started with the goal "build something that helps me" and graduated to a lightweight testing ground for AI-enabled IDE features. Read more about the origin:\nhttps://reactiverobot.com/writing/leaving-figma.html\n\nImportant notes before you try using this in your own codebase:\n- It will work best in a Typescript codebase. Most of my projects are in Typescript, so I\'ve targeted that first.\n- This is all experimental so glhf.\n\nWhy is it called AlphaWolf?\n- It\'s named after my best friend and coding buddy ZenPablo aka Mr Snuggles aka AlphaWolf.\n~~~~~~~~~~~~~~~~',
  )
  .command(
    'setup',
    'Setup api keys',
    (yargs) => commonOptions(yargs),
    () => {
      setupSettings()
    },
  )
  .command(
    'generate',
    'Generate code from a prompt',
    (yargs) =>
      llmOptions(commonOptions(yargs), Gpt5Model.name)
        .option('prompt', {
          type: 'string',
          describe: 'The prompt describing the code to generate',
          alias: 'p',
        })
        .option('b', {
          boolean: true,
          default: false,
          describe: 'Use the paste buffer as the prompt',
          alias: 'paste',
        })
        .option('url', {
          type: 'string',
          describe: 'URL of markdown content to include in context',
          alias: 'u',
        })
        .option('subdir', {
          type: 'array',
          describe: 'List of subdirectories to limit context to',
          alias: 's',
        })
        .option('context', {
          type: 'string',
          choices: Object.keys(ContextProviders),
          default: 'documentation',
          describe: 'Choose which context provider to use. `awolf man contextTypes` for more info.',
        })
        .option('docs', {
          type: 'string',
          describe: 'Path to a directory containing documentation files',
        }),
    (argv) => {
      handleCommand(argv, (directory, prompt, model) => {
        requireSettings([
          ...model.getApi().requiredKeys,
          ...(ContextProviderApiKeys[argv.context as keyof typeof ContextProviders] || []),
          'FIREWORKS_API_KEY',
        ])

        const sparsePath = argv.subdir
          ? { path: directory, allowedSubPaths: argv.subdir as string[] }
          : directory
        info_block(
          'Working in',
          typeof sparsePath === 'string'
            ? sparsePath
            : sparsePath.allowedSubPaths?.map((p) => path.join(sparsePath.path, p)).join('\n'),
        )

        generateCodeWithStreamV2(
          sparsePath,
          prompt,
          model,
          argv.context as keyof typeof ContextProviders,
          argv.url as string,
          argv.docs as string,
        )
      })
    },
  )
  .command(
    'ask',
    'Ask a question about the codebase',
    (yargs) =>
      llmOptions(commonOptions(yargs), ClaudeSonnetModel.name)
        .option('q', {
          alias: 'question',
          type: 'string',
          describe: 'The question to ask about the codebase',
          demandOption: true,
        })
        .option('context', {
          type: 'string',
          choices: Object.keys(ContextProviders),
          default: 'documentation',
          describe: 'Choose which context provider to use. `awolf man contextTypes` for more info.',
        })
        .option('subdir', {
          type: 'array',
          describe: 'List of subdirectories to limit context to',
          alias: 's',
        })
        .option('docs', {
          type: 'string',
          describe: 'Path to a directory containing documentation files',
        })
        .option('url', {
          type: 'string',
          describe: 'URL of markdown content to include in context',
          alias: 'u',
        }),
    (argv) => {
      handleCommand(argv, (directory, _, model) => {
        requireSettings([
          ...model.getApi().requiredKeys,
          ...(ContextProviderApiKeys[argv.context as keyof typeof ContextProviders] || []),
        ])

        const sparsePath = argv.subdir
          ? { path: directory, allowedSubPaths: argv.subdir as string[] }
          : directory
        info_block(
          'Looking at',
          typeof sparsePath === 'string'
            ? sparsePath
            : sparsePath.allowedSubPaths?.map((p) => path.join(sparsePath.path, p)).join('\n'),
        )
        askQuestion(
          sparsePath,
          argv.question as string,
          model,
          argv.context as keyof typeof ContextProviders,
          argv.docs as string,
          argv.url as string,
        )
      })
    },
  )
  .command(
    'commit',
    'git commit with AI generated message',
    (yargs) =>
      llmOptions(commonOptions(yargs), ClaudeHaikuModel.name).option('a', {
        alias: 'all',
        type: 'boolean',
        describe: 'Stage all tracked files with modifications and deletions before committing',
      }),
    (argv) => {
      handleCommand(argv, (directory, _, model) => {
        requireSettings([...model.getApi().requiredKeys])
        writeCommitMessage(directory, model, argv.all as boolean)
      })
    },
  )
  .command(
    'undo',
    'Undo the most recent operation',
    (yargs) =>
      commonOptions(yargs).option('m', {
        alias: 'mode',
        type: 'string',
        choices: ['strict', 'overwrite'],
        default: 'strict',
        describe:
          'Strict will error if the original lines are not as expected. Overwrite will apply the changes, regardless of lines being replaced.',
      }),
    (argv) => {
      handleCommand(argv, async (directory) => {
        const undoRedo = new UndoRedo(directory)
        await undoRedo.undo(argv.mode as 'strict' | 'overwrite')
      })
    },
  )
  .command(
    'redo',
    'Redo the most recently undone operation',
    (yargs) =>
      commonOptions(yargs).option('m', {
        alias: 'mode',
        type: 'string',
        choices: ['strict', 'overwrite'],
        default: 'strict',
        describe:
          'Strict will error if the original lines are not as expected. Overwrite will apply the changes, regardless of lines being replaced.',
      }),
    (argv) => {
      handleCommand(argv, async (directory) => {
        const undoRedo = new UndoRedo(directory)
        await undoRedo.redo(argv.mode as 'strict' | 'overwrite')
      })
    },
  )
  .command(
    'history',
    'Show the undo/redo history',
    (yargs) =>
      commonOptions(yargs)
        .option('n', {
          alias: 'number',
          type: 'number',
          describe: 'Number of recent operations to show',
          default: 1,
        })
        .option('s', {
          alias: 'stack',
          type: 'string',
          describe: 'Which stack to show: "undo" or "redo"',
          default: 'undo',
          choices: ['undo', 'redo'],
        })
        .option('clear', {
          boolean: true,
          describe: 'Clear the history',
        }),
    (argv) => {
      handleCommand(argv, async (directory) => {
        const undoRedo = new UndoRedo(directory)
        if (argv.clear) {
          undoRedo.clear()
          print_ai('History cleared.')
        } else {
          undoRedo.printHistory(argv.n as number, argv.stack as 'undo' | 'redo')
        }
      })
    },
  )
  .command(
    'watch',
    '[WIP] Watch a directory for changes. Works best with VSCode files.autoSave=afterDelay and files.autoSaveDelay=100 so that watcher detects changes as soon you make them.',
    (yargs) =>
      commonOptions(yargs)
        .option('mode', {
          type: 'string',
          describe:
            'The mode to use: "comment", "log", "multicomplete", or "autocomplete". `awolf man watchModes` for more info',
          default: 'log',
          choices: ['comment', 'log', 'multicomplete', 'autocomplete'],
        })
        .option('ms', {
          type: 'number',
          describe: 'Debounce delay while receiving changes',
          default: 1000,
        }),
    (argv) => {
      handleCommand(argv, async (directory) => {
        await watchDirectory(directory, argv.mode as WatchMode, argv.ms as number)
      })
    },
  )
  .command(
    'index',
    '[WIP] Index the codebase for searching',
    (yargs) =>
      commonOptions(yargs)
        .command(
          'lexical',
          'Index using lexical analysis',
          (yargs: any) => indexOptions(commonOptions(yargs)),
          (argv: any) => {
            handleCommand(argv, async (directory) => {
              await doLexicalIndex(directory, argv.query as string)
            })
          },
        )
        .command(
          'embedding',
          'Index using embeddings',
          (yargs: any) =>
            indexOptions(commonOptions(yargs))
              .option('embedder', {
                type: 'string',
                describe: 'The embedder to use. Default: ' + DefaultEmbedderName,
                alias: 'e',
              })
              .option('cache-key', {
                type: 'string',
                describe: 'The cache key to use for the index',
                alias: 'c',
              }),
          (argv: any) => {
            handleCommand(argv, async (directory) => {
              await doEmbeddingIndex(
                directory,
                argv.query as string,
                argv.embedder as EmbeddingName,
                argv.cacheKey as string,
              )
            })
          },
        )
        .demandCommand(1),
    () => {},
  )
  .command(
    'todo',
    '[WIP] Complete all TODOs matching a TODO phrase (default: "TODO (awolf):")',
    (yargs) =>
      llmOptions(commonOptions(yargs))
        .option('phrase', {
          type: 'string',
          describe: 'The TODO phrase to use. Default: "TODO (awolf):"',
        })
        .option('prompt', {
          type: 'string',
          describe: 'Additional guidance for completing the TODOs',
          alias: 'p',
        })
        .option('file', {
          type: 'string',
          describe: 'Specific file to process TODOs in',
          alias: 'f',
        }),
    (argv) => {
      handleCommand(argv, (directory, _, model) => {
        completeTodos(
          directory,
          model,
          generateCodeWithStreamV2,
          argv.phrase as string,
          argv.prompt as string,
          argv.file as string,
        )
      })
    },
  )
  .command(
    'finish',
    '[WIP] Look at a diff and complete it',
    (yargs) =>
      llmOptions(commonOptions(yargs))
        .option('prompt', {
          type: 'string',
          describe: 'The prompt to guide the diff completion',
          alias: 'p',
        })
        .option('add', {
          type: 'boolean',
          describe: 'Add all changed files to the index before finishing',
          alias: 'a',
          default: false,
        }),
    (argv) => {
      handleCommand(argv, (directory, prompt, model) => {
        finishDiff(directory, model, prompt, argv.add as boolean)
      })
    },
  )

  .command(
    'fix',
    '[WIP] Fix issues in the codebase',
    (yargs) =>
      llmOptions(commonOptions(yargs)).option('c', {
        alias: 'check',
        type: 'string',
        describe: 'The check to run (e.g., "lint", "test")',
        demandOption: true,
      }),
    (argv) => {
      handleCommand(argv, (directory, _, model, check) => {
        if (check) {
          requireSettings([...model.getApi().requiredKeys])
          doFix(directory, model, check)
        } else {
          exit_with_error('The --check (-c) option is required for the fix command.')
        }
      })
    },
  )
  .command(
    'document',
    '[WIP] Add or update @fileoverview comments in JS/TS files',
    (yargs) =>
      llmOptions(commonOptions(yargs))
        .option('files', {
          type: 'array',
          describe: 'Specific files to document instead of a directory',
          alias: 'f',
        })
        .option('mode', {
          type: 'string',
          choices: ['insert', 'update'],
          default: 'insert',
          describe: 'insert: only add missing comments, update: add or update all comments',
        })
        .option('dry-run', {
          type: 'boolean',
          describe: 'Estimate cost without making changes',
          default: false,
        }),
    (argv) => {
      handleCommand(argv, (directory, _, model) => {
        requireSettings([...model.getApi().requiredKeys])
        documentFiles(
          directory,
          model,
          argv.files as string[],
          argv.mode as 'insert' | 'update',
          argv.dryRun as boolean,
        )
      })
    },
  )
  .command(
    'change-log <hash1> <hash2>',
    'Generate a change log from git commit history',
    (yargs) =>
      llmOptions(commonOptions(yargs))
        .positional('hash1', {
          type: 'string',
          describe: 'Starting commit hash/tag/branch',
        })
        .positional('hash2', {
          type: 'string',
          describe: 'Ending commit hash/tag/branch',
        })
        .option('format', {
          type: 'string',
          describe: 'Output format',
          choices: ['markdown', 'text', 'json'],
          default: 'markdown',
        }),
    (argv) => {
      handleCommand(argv, (directory, _, model) => {
        requireSettings([...model.getApi().requiredKeys])
        generateChangeLog(
          directory,
          model,
          argv.hash1 as string,
          argv.hash2 as string,
          argv.format as 'markdown' | 'text' | 'json',
        )
      })
    },
  )
  .command(
    'agent',
    '[very WIP] Start an agent loop that uses AI to choose and execute tools',
    (yargs) =>
      commonOptions(yargs)
        .option('prompt', {
          type: 'string',
          describe: 'Initial prompt for the agent',
          alias: 'p',
        })
        .option('max-iter', {
          type: 'number',
          describe: 'Maximum number of iterations',
          default: 15,
        }),
    (argv) => {
      handleCommand(argv, async (_, prompt) => {
        requireSettings(['ANTHROPIC_API_KEY'])
        await runAgentLoop(prompt, argv.maxIter as number)
      })
    },
  )
  .command(
    'man',
    'Show manual pages for alpha-wolf commands and features',
    (yargs) =>
      commonOptions(yargs)
        .command(
          'contextTypes',
          'Show available context providers and what they do',
          (yargs: any) => yargs,
          () => {
            printContextTypes()
          },
        )
        .command(
          'watchModes',
          'Show available watch modes and what they do',
          (yargs: any) => yargs,
          () => {
            printWatchModes()
          },
        )
        .demandCommand(1),
    () => {},
  )
  .demandCommand(1)
  .parse()
